import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import {
  User,
  Activity as ActivityIcon,
  Bell,
  History,
  Check,
  Shield,
  Camera,
} from 'lucide-react';
import { useAgentStore, type AgentProfile } from '../stores/agent';
import { useNotificationsStore, type ToastKind } from '../stores/notifications';
import { pushPermission, requestPushPermission } from '../lib/pwa';
import { ensurePushSubscription } from '../lib/push';
import { browserTimeZone, supportedTimeZones } from '../lib/datetime';
import { serverOrigin } from '../lib/server-origin';
import { PhoneInput, isValidE164 } from '../components/PhoneInput';
import {
  Button,
  Badge,
  Input,
  Label,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Avatar,
  AvatarImage,
  AvatarFallback,
} from '../components/ui';
import styles from './Settings.module.css';
import { type AgentAvailability } from '@prakash-software/shared';

const AVAIL: readonly AgentAvailability[] = ['available', 'away', 'offline'];

const KIND_LABEL: Record<ToastKind, { title: string; desc: string }> = {
  visitor: { title: 'New visitor arrived', desc: 'When a new visitor connects and loads widget' },
  chat: { title: 'Incoming chat request', desc: 'When a visitor sends a first message or asks for help' },
  escalate: { title: 'AI → human escalation', desc: 'When AI hands over conversation to human agents' },
  disconnect: { title: 'Visitor disconnected', desc: 'When an active chat visitor leaves the session' },
  system: { title: 'System events', desc: 'Maintenance notices, server bus changes and updates' },
};

const TIME_FMT = new Intl.DateTimeFormat([], { hour: '2-digit', minute: '2-digit', hour12: false });

export function Settings() {
  const agent = useAgentStore((s) => s.current);
  const availability = useAgentStore((s) => s.availability);
  const timezone = useAgentStore((s) => s.timezone);
  const setAvailability = useAgentStore((s) => s.setAvailability);
  const setTimezone = useAgentStore((s) => s.setTimezone);

  const prefs = useNotificationsStore((s) => s.prefs);
  const setPrefs = useNotificationsStore((s) => s.setPrefs);
  const audit = useNotificationsStore((s) => s.audit);
  const [tab, setTab] = useState('profile');

  if (!agent) return null;

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <div className="flex items-center gap-2 mb-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            <span className="w-2 h-2 rounded-full bg-blue-500 shadow-sm shadow-blue-500/50" />
            <span>Agent Workspace · Preferences</span>
          </div>
          <h1 className={styles.title}>Account & Preferences</h1>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="profile" className="gap-1.5 text-xs">
            <User size={13} />
            <span>Profile</span>
          </TabsTrigger>
          <TabsTrigger value="availability" className="gap-1.5 text-xs">
            <ActivityIcon size={13} />
            <span>Availability</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-1.5 text-xs">
            <Bell size={13} />
            <span>Notifications</span>
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-1.5 text-xs">
            <History size={13} />
            <span>Activity Log</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className={styles.settingsCard}>
            <Identity agent={agent} />
          </div>
        </TabsContent>

        <TabsContent value="availability">
          <div className={styles.settingsCard}>
            <div className={styles.row}>
              <span className={styles.label}>Working Status</span>
              <div className={styles.availGroup}>
                {AVAIL.map((value) => {
                  const isCurrent = availability === value;
                  const activeClass = isCurrent
                    ? value === 'available'
                      ? styles.availBtnActiveAvailable
                      : value === 'away'
                        ? styles.availBtnActiveAway
                        : styles.availBtnActiveOffline
                    : '';
                  return (
                    <button
                      key={value}
                      type="button"
                      className={`${styles.availBtn} ${activeClass}`}
                      onClick={() => setAvailability(value)}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor:
                            value === 'available'
                              ? '#10b981'
                              : value === 'away'
                                ? '#f59e0b'
                                : '#94a3b8',
                        }}
                      />
                      <span style={{ textTransform: 'capitalize' }}>{value}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <hr className={styles.rule} data-label="TIMEZONE" />
            <div className={styles.row}>
              <label className={styles.label} htmlFor="tz">
                Display timezone
              </label>
              <select
                id="tz"
                className={styles.timezoneSelect}
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                <option value="">Follow browser ({browserTimeZone()})</option>
                {supportedTimeZones().map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
            </div>
            <p className={styles.help}>
              All visible timestamps across chats, live visitor matrices, and audit logs display in this timezone.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="notifications">
          <div className={styles.settingsCard}>
            <PushSection />

            <hr className={styles.rule} data-label="ALERTS & AUDIO" />
            <div className={styles.row}>
              <div>
                <span className={styles.label}>In-App Toast Alerts</span>
                <p className="text-xs text-muted-foreground mt-0.5">Show animated notifications on the screen</p>
              </div>
              <Switch
                checked={prefs.toastsEnabled}
                onCheckedChange={(v) => setPrefs({ toastsEnabled: v })}
              />
            </div>
            <div className={styles.row}>
              <div>
                <span className={styles.label}>Sound Chime</span>
                <p className="text-xs text-muted-foreground mt-0.5">Play audio notification on new messages</p>
              </div>
              <Switch
                checked={prefs.soundEnabled}
                onCheckedChange={(v) => setPrefs({ soundEnabled: v })}
              />
            </div>

            <hr className={styles.rule} data-label="EVENT SUBSCRIPTIONS" />
            <div className={styles.eventGrid}>
              {(Object.keys(KIND_LABEL) as ToastKind[]).map((kind) => (
                <label
                  key={kind}
                  className={styles.eventCard}
                >
                  <input
                    type="checkbox"
                    className={styles.eventCheckbox}
                    checked={prefs.perKind[kind]}
                    onChange={(e) =>
                      setPrefs({ perKind: { ...prefs.perKind, [kind]: e.target.checked } })
                    }
                  />
                  <div className={styles.eventBody}>
                    <span className={styles.eventTitle}>{KIND_LABEL[kind].title}</span>
                    <span className={styles.eventDesc}>{KIND_LABEL[kind].desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <div className={styles.settingsCard}>
            <div className={styles.activityHeader}>
              <span className={styles.activityTitle}>Recent Agent Activity</span>
              <Badge variant="outline" className="text-[11px] font-mono">
                {audit.length} events
              </Badge>
            </div>
            {audit.length === 0 ? (
              <p className={styles.help}>No activity recorded in this session yet.</p>
            ) : (
              <ul className={styles.auditList}>
                {audit.map((entry) => (
                  <li key={entry.id} className={styles.auditEntry}>
                    <time className={styles.auditTime} dateTime={new Date(entry.at).toISOString()}>
                      {TIME_FMT.format(new Date(entry.at))}
                    </time>
                    <span className="text-xs text-foreground font-medium">{entry.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
}

function Identity({ agent }: { agent: AgentProfile }) {
  const updateProfile = useAgentStore((s) => s.updateProfile);
  const uploadAvatar = useAgentStore((s) => s.uploadAvatar);
  const [name, setName] = useState(agent.name);
  const [phone, setPhone] = useState(agent.phone ?? '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const nameOk = name.trim().length > 0;
  const phoneOk = isValidE164(phone);
  const dirty =
    (name.trim() !== agent.name || phone !== (agent.phone ?? '')) && nameOk && phoneOk;
  const avatarSrc = agent.avatarUrl
    ? new URL(agent.avatarUrl, serverOrigin()).toString()
    : null;

  async function save() {
    setError(null);
    setOkMsg(null);
    setSaving(true);
    const res = await updateProfile(name.trim(), phone);
    setSaving(false);
    if (res.ok) setOkMsg('Saved.');
    else setError(res.reason === 'invalid-name' ? 'Check name and phone.' : 'Save failed.');
  }

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setOkMsg(null);
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      setError('Use a PNG, JPEG, or WebP.');
      return;
    }
    if (file.size > 1024 * 1024) {
      setError('Avatar must be under 1 MB.');
      return;
    }
    setUploading(true);
    const res = await uploadAvatar(file);
    setUploading(false);
    if (res.ok) setOkMsg('Avatar updated.');
    else
      setError(
        res.reason === 'too-large'
          ? 'Avatar must be under 1 MB.'
          : res.reason === 'unsupported-type'
            ? 'Use a PNG, JPEG, or WebP.'
            : 'Upload failed.',
      );
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <div>
      <div className={styles.profileHeader}>
        <div className={styles.avatarWrapper}>
          <Avatar className={styles.avatarLarge}>
            {avatarSrc ? <AvatarImage src={avatarSrc} alt={agent.name} /> : null}
            <AvatarFallback>{agent.initials}</AvatarFallback>
          </Avatar>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className={styles.avatarBadgeBtn}
            title="Change photo"
          >
            <Camera size={13} />
          </button>
        </div>

        <div className={styles.profileMeta}>
          <div className={styles.profileTitleRow}>
            <h2 className={styles.profileNameHeading}>{agent.name}</h2>
            <span className={styles.roleBadge}>
              <Shield size={11} className="mr-1" />
              {agent.role.replace('-', ' ')}
            </span>
          </div>
          <p className={styles.profileEmailRow}>
            <span>{agent.email}</span>
            <span className={styles.emailLockBadge}>Managed by admin</span>
          </p>
        </div>
      </div>

      <div className={styles.profileFieldsGrid}>
        <div className={styles.fieldGroup}>
          <Label htmlFor="profile-name" className={styles.fieldLabel}>
            Display Name
          </Label>
          <Input
            id="profile-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            autoComplete="off"
            className={styles.fieldInput}
            placeholder="Your display name"
          />
        </div>

        <div className={styles.fieldGroup}>
          <Label htmlFor="profile-phone" className={styles.fieldLabel}>
            WhatsApp Contact Number
          </Label>
          <div className={styles.phoneInputContainer}>
            <PhoneInput
              id="profile-phone"
              value={phone}
              onChange={setPhone}
              selectClassName={styles.fieldInput}
              inputClassName={styles.fieldInput}
            />
          </div>
        </div>
      </div>

      <div className={styles.profileActions}>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className={styles.fileInput}
          onChange={(e) => void onFile(e)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className={styles.btnUploadPhoto}
        >
          <Camera size={14} />
          <span>{uploading ? 'Uploading…' : avatarSrc ? 'Change photo' : 'Upload photo'}</span>
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => void save()}
          disabled={!dirty || saving}
          className={styles.btnSaveChanges}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
      {error ? <p className="text-xs text-destructive font-medium mt-2">{error}</p> : null}
      {okMsg ? <p className="text-xs text-emerald-500 font-medium mt-2">{okMsg}</p> : null}
    </div>
  );
}

function PushSection() {
  const [perm, setPerm] = useState<NotificationPermission | 'unsupported'>('default');
  useEffect(() => {
    setPerm(pushPermission());
  }, []);

  async function enable() {
    const next = await requestPushPermission();
    setPerm(next);
    // Register this browser for closed-tab Web Push once permission lands.
    if (next === 'granted') void ensurePushSubscription();
  }

  if (perm === 'unsupported') {
    return (
      <p className={styles.help}>
        This browser doesn’t expose the Notifications API — toasts still work in-app.
      </p>
    );
  }

  return (
    <div className={styles.row}>
      <div>
        <span className={styles.label}>Browser Push Notifications</span>
        <p className="text-xs text-muted-foreground mt-0.5">Receive alerts when the tab is running in the background</p>
      </div>
      {perm === 'granted' ? (
        <Badge variant="outline" className="text-xs text-emerald-500 border-emerald-500/20 bg-emerald-500/10">
          <Check size={11} className="mr-1" /> Granted
        </Badge>
      ) : perm === 'denied' ? (
        <Badge variant="destructive" className="text-xs">
          Blocked in Browser
        </Badge>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={enable} className="h-8 text-xs">
          Enable Browser Push
        </Button>
      )}
    </div>
  );
}

